import React, { ReactNode } from "react";

import Form from "antd/es/form";
import { defineMessages, useIntl } from "react-intl";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import Scoped from "@app/scene/dashboard/types/Scoped";

import SwitchLine from "./components/SwitchLine";

const messages = defineMessages({
  chartLine: {
    id: "dashboard.editModal.preferences.default.chartLine",
    defaultMessage: "Chart Line",
    description:
      "Title of the Chart Line field in a preferences form in Edit New Records Modal on Dashboard",
  },
  chartLineTooltip: {
    id: "dashboard.editModal.preferences.default.chartLine.tooltip",
    defaultMessage:
      "When enabled, a chart line will display values over time within the selected period, in addition to the total count.",
    description:
      "Tooltip for the Chart Line field in a preferences form in Edit New Records Modal on Dashboard",
  },
  cumulative: {
    id: "dashboard.editModal.preferences.default.cumulative.",
    defaultMessage: "Cumulative",
    description:
      "Title for the Chart Line cumulative field in a preferences form in Edit New Records Modal on Dashboard",
  },
  cumulativeTooltip: {
    id: "dashboard.editModal.preferences.default.cumulative.tooltip",
    defaultMessage:
      "When enabled, shows the sum of values for the selected time range at a particular time",
    description:
      "Tooltip for the Chart Line cumulative field in a preferences form in Edit New Records Modal on Dashboard",
  },
  rankPosition: {
    id: "dashboard.editModal.preferences.default.rankPosition",
    defaultMessage: "Rank position",
    description:
      "Title of the Rank Position field in a preferences form in Edit New Records Modal on Dashboard",
  },
  rankPositionTooltip: {
    id: "dashboard.editModal.preferences.default.rankPosition.tooltip",
    defaultMessage: "When enabled, see the user or team’s stack rank position for the metric.",
    description:
      "Tooltip for the Rank Position field in a preferences form in Edit New Records Modal on Dashboard",
  },
  trend: {
    id: "dashboard.editModal.preferences.default.trend",
    defaultMessage: "Trend",
    description:
      "Title of the Trend field in a preferences form in Edit New Records Modal on Dashboard",
  },
  trendTooltip: {
    id: "dashboard.editModal.preferences.default.trend.tooltip",
    defaultMessage: "When enabled, see the % change up or down from the previous period.",
    description:
      "Tooltip for the Trend field in a preferences form in Edit New Records Modal on Dashboard",
  },
  verifiedBreakdown: {
    id: "dashboard.editModal.preferences.default.verifiedBreakdown",
    defaultMessage: "Verified Breakdown",
    description:
      "Title of the Verified Breakdown field in a preferences form in Edit New Records Modal on Dashboard",
  },
  verifiedBreakdownTooltip: {
    id: "dashboard.editModal.preferences.default.verifiedBreakdown.tooltip",
    defaultMessage:
      "When enabled, see a pie chart that breaks down verified check-ins versus un-verified check-ins",
    description:
      "Tooltip for the Verified Breakdown field in a preferences form in Edit New Records Modal on Dashboard",
  },
});

interface Props extends Scoped {
  disableCumulative?: boolean;
  extraChartPreferences?: ReactNode;
  showRankPosition?: boolean;
  showVerifiedBreakdown?: boolean;
}

const DefaultPreferencesFormComponent: React.FC<Props> = ({
  disableCumulative = false,
  extraChartPreferences,
  scope,
  showRankPosition = false,
  showVerifiedBreakdown,
}) => {
  const intl = useIntl();

  return (
    <div>
      <Form.Item name={["preferences", "showChart"]}>
        <SwitchLine
          label={
            <TextWithInfo info={intl.formatMessage(messages.chartLineTooltip)} placement="top">
              {intl.formatMessage(messages.chartLine)}
            </TextWithInfo>
          }
        />
      </Form.Item>
      {extraChartPreferences}
      <Form.Item name={["preferences", "cumulative"]}>
        <SwitchLine
          disabled={disableCumulative}
          label={
            <TextWithInfo info={intl.formatMessage(messages.cumulativeTooltip)} placement="top">
              {intl.formatMessage(messages.cumulative)}
            </TextWithInfo>
          }
        />
      </Form.Item>
      {showVerifiedBreakdown && (
        <Form.Item name={["preferences", "verifiedBreakdown"]}>
          <SwitchLine
            label={
              <TextWithInfo
                info={intl.formatMessage(messages.verifiedBreakdownTooltip)}
                placement="top"
              >
                {intl.formatMessage(messages.verifiedBreakdown)}
              </TextWithInfo>
            }
          />
        </Form.Item>
      )}
      {!!scope.teamId && showRankPosition && (
        <Form.Item name={["preferences", "showRankPosition"]}>
          <SwitchLine
            label={
              <TextWithInfo info={intl.formatMessage(messages.rankPositionTooltip)} placement="top">
                {intl.formatMessage(messages.rankPosition)}
              </TextWithInfo>
            }
          />
        </Form.Item>
      )}
      <Form.Item name={["preferences", "showTrend"]}>
        <SwitchLine
          label={
            <TextWithInfo info={intl.formatMessage(messages.trendTooltip)} placement="top">
              {intl.formatMessage(messages.trend)}
            </TextWithInfo>
          }
        />
      </Form.Item>
    </div>
  );
};

export default DefaultPreferencesFormComponent;
